import { Box, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import { extractNestHttpErrorMessage } from "@shared/exceptions-message";
import { linko } from "@shared/linko";
import { useState, type FC, type FormEvent } from "react";
import { useAlert } from "../../contexts/AlertProvider";
import { useCallCenterShift } from "../../contexts/CallCenterShiftProvider";
import { CC_BRAND } from "./call-center-brand";
import { CcButton, CcEyebrow, CcProgress, CcStageCard } from "./CallCenterUi";
import { CC, ccFieldSx, navyStageSx } from "./call-center-ui.theme";

export const CallCenterShiftLogin: FC = () => {
  const { applyWorkbench } = useCallCenterShift();
  const { setAlertText, setSeverity } = useAlert();
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);

  const startShift = async (event?: FormEvent) => {
    event?.preventDefault();
    if (phone.length !== 10 || loading) return;
    setLoading(true);
    try {
      const { data } = await linko.callCenter.mutations.startShift({
        phoneNumber: phone as `+972${number}`,
      });
      applyWorkbench(data);
    } catch (error) {
      const message = extractNestHttpErrorMessage(error);
      setSeverity("error");
      setAlertText(
        message && /[\u0590-\u05FF]/.test(message)
          ? message
          : "מספר הטלפון אינו רשום כמתנדב מוקד",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={navyStageSx}>
      <CcStageCard>
        <Stack component="form" onSubmit={(event) => void startShift(event)} gap={3}>
          <Stack gap={1.25}>
            <CcEyebrow>{CC_BRAND}</CcEyebrow>
            <Typography
              sx={{
                fontFamily: CC.font,
                fontSize: { xs: 28, md: 32 },
                fontWeight: 700,
                lineHeight: 1.15,
                letterSpacing: "-0.03em",
                color: CC.ink,
              }}
            >
              שלום, מה מספר הטלפון שלך?
            </Typography>
            <Typography sx={{ fontFamily: CC.font, color: CC.inkMuted, lineHeight: 1.6 }}>
              מגייסים מתנדבים פרטניים וקבוצות. הזינו מספר כדי להתחיל משמרת. העמדה כבר מחוברת, בלי קוד ובלי SMS.
            </Typography>
          </Stack>
          <TextField
            label="מספר טלפון"
            value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
            inputProps={{ maxLength: 10, dir: "ltr", inputMode: "numeric" }}
            autoFocus
            fullWidth
            sx={ccFieldSx}
          />
          <CcButton
            type="submit"
            disabled={phone.length !== 10 || loading}
            fullWidth
            sx={{ py: 1.4 }}
          >
            {loading ? "נכנסים..." : "כניסה למשמרת"}
          </CcButton>
        </Stack>
      </CcStageCard>
    </Box>
  );
};

export const CallCenterBriefing: FC = () => {
  const { workbench, applyWorkbench, setPendingFirstTour } = useCallCenterShift();
  const [busy, setBusy] = useState(false);
  const isFirst = !workbench?.volunteer.firstShiftAt;

  const continueShift = async () => {
    setBusy(true);
    try {
      const { data } = await linko.callCenter.mutations.markBriefingSeen();
      applyWorkbench(data);
      if (isFirst) setPendingFirstTour(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={navyStageSx}>
      <CcStageCard wide>
        <Stack gap={3}>
          <Stack gap={1.25}>
            <CcEyebrow>{CC_BRAND}</CcEyebrow>
            <Typography
              sx={{
                fontFamily: CC.font,
                fontSize: { xs: 28, md: 32 },
                fontWeight: 700,
                letterSpacing: "-0.03em",
                color: CC.ink,
              }}
            >
              דגשים למשמרת
            </Typography>
            <Typography sx={{ fontFamily: CC.font, color: CC.inkMuted }}>
              {workbench?.volunteer.name
                ? `${workbench.volunteer.name}, קריאה קצרה ואפשר לצאת לדרך.`
                : "קריאה קצרה ואפשר לצאת לדרך."}
            </Typography>
          </Stack>
          <Box
            sx={{
              whiteSpace: "pre-wrap",
              p: 2.5,
              borderRadius: `${CC.radiusSm}px`,
              bgcolor: "rgba(5,32,76,0.04)",
              border: `1px solid ${CC.line}`,
              fontFamily: CC.font,
              color: CC.ink,
              lineHeight: 1.7,
            }}
          >
            {workbench?.settings.fixedBriefing || "אין דגשים קבועים כרגע."}
          </Box>
          {workbench?.settings.rotatingNotes ? (
            <Box
              sx={{
                whiteSpace: "pre-wrap",
                p: 2.5,
                borderRadius: `${CC.radiusSm}px`,
                bgcolor: CC.goldSoft,
                border: "1px solid rgba(165,124,50,0.22)",
                fontFamily: CC.font,
                color: CC.ink,
                lineHeight: 1.7,
              }}
            >
              <CcEyebrow>היום / השבוע</CcEyebrow>
              <Box sx={{ mt: 1 }}>{workbench.settings.rotatingNotes}</Box>
            </Box>
          ) : null}
          <CcButton disabled={busy} onClick={() => void continueShift()} sx={{ alignSelf: "flex-start", px: 3.5 }}>
            {busy ? "ממשיכים..." : "הבנתי, ממשיכים"}
          </CcButton>
        </Stack>
      </CcStageCard>
    </Box>
  );
};

export const CallCenterGoalScreen: FC = () => {
  const { workbench, applyWorkbench, setPendingFirstTour } = useCallCenterShift();
  const [busy, setBusy] = useState(false);
  const matches = workbench?.shift.matchesCount ?? 0;
  const goal = workbench?.volunteer.dailyGoal ?? 0;

  return (
    <Box sx={navyStageSx}>
      <CcStageCard>
        <Stack gap={3.5} alignItems="stretch">
          <Stack gap={1.25}>
            <CcEyebrow>{CC_BRAND}</CcEyebrow>
            <Typography
              sx={{
                fontFamily: CC.font,
                fontSize: { xs: 28, md: 32 },
                fontWeight: 700,
                letterSpacing: "-0.03em",
                color: CC.ink,
              }}
            >
              היעד למשמרת
            </Typography>
          </Stack>
          <Stack alignItems="center" gap={1.5} sx={{ py: 1 }}>
            <Typography
              sx={{
                fontFamily: CC.font,
                fontSize: 72,
                fontWeight: 700,
                letterSpacing: "-0.06em",
                lineHeight: 1,
                color: CC.ink,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {matches}
              <Box
                component="span"
                sx={{ color: CC.inkMuted, fontSize: 36, fontWeight: 500, mx: 0.8 }}
              >
                /
              </Box>
              {goal}
            </Typography>
            <Typography sx={{ fontFamily: CC.font, color: CC.inkMuted }}>
              מאצ'ים. המספר מתעדכן אחרי כל שידוך.
            </Typography>
          </Stack>
          <CcProgress value={matches} max={goal} />
          <CcButton
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                const isFirst = !workbench?.volunteer.firstShiftAt;
                const { data } = await linko.callCenter.mutations.markGoalSeen();
                applyWorkbench(data);
                if (isFirst) setPendingFirstTour(true);
              } finally {
                setBusy(false);
              }
            }}
            fullWidth
            sx={{ py: 1.4 }}
          >
            {busy ? "פותחים..." : "קדימה לעבודה"}
          </CcButton>
        </Stack>
      </CcStageCard>
    </Box>
  );
};

export const CallCenterFarewell: FC<{ message: string; onClose: () => void }> = ({
  message,
  onClose,
}) => (
  <Box sx={navyStageSx}>
    <CcStageCard>
      <Stack gap={3}>
        <CcEyebrow>{CC_BRAND}</CcEyebrow>
        <Typography
          sx={{
            fontFamily: CC.font,
            fontSize: { xs: 28, md: 32 },
            fontWeight: 700,
            letterSpacing: "-0.03em",
            color: CC.ink,
          }}
        >
          תודה על המשמרת
        </Typography>
        <Typography sx={{ fontFamily: CC.font, color: CC.ink, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
          {message}
        </Typography>
        <CcButton onClick={onClose} fullWidth sx={{ py: 1.4 }}>
          סיום
        </CcButton>
      </Stack>
    </CcStageCard>
  </Box>
);

export const CallCenterStationLoading: FC = () => (
  <Box sx={navyStageSx}>
    <Stack alignItems="center" gap={2}>
      <CircularProgress size={28} sx={{ color: CC.paper }} />
      <Typography sx={{ fontFamily: CC.font, color: "rgba(255,252,247,0.72)" }}>
        טוענים את העמדה
      </Typography>
    </Stack>
  </Box>
);
